import { motion } from 'framer-motion';
import { FaBaby, FaSwimmer, FaTrophy, FaMedal, FaStar, FaClock, FaUsers, FaCheckCircle } from 'react-icons/fa';
import { GiSwimfins } from 'react-icons/gi';
import Seo from '../components/Seo';

function Programs() {
  const programs = [
    {
      icon: <FaBaby className="text-5xl" />,
      title: 'Beginners - Learning to Swim',
      color: 'blue',
      sessions: 'Sunday 2:00 p.m. – 3:00 p.m.',
      classSize: 'Maximum 10 children per qualified teacher',
      description: 'There are eight Beginners classes at Marple Swimming Club. Class numbers are kept to a maximum of ten children to one qualified teacher. Teachers are also assisted by in the water helpers to further increase your child\'s confidence and progression. Children swim across the pool in widths. We follow the nationally recognised Swim England swimming curriculum and offer badges and certificates from the associated award scheme.'
    },
    {
      icon: <FaSwimmer className="text-5xl" />,
      title: 'Improvers',
      color: 'green',
      sessions: 'Wednesdays 7:00 p.m. – 7:30 p.m., Sundays 3:00 p.m. – 3:30 p.m.',
      description: 'In this group children will swim the pool in lengths. Teachers will be working mainly on stroke correction and helping children to build their stamina. Children will also be introduced to some of the basic concepts of racing and competition. Children from this group will feed into our Development and competitive swimming groups, at the appropriate time. We follow the nationally recognised Swim England swimming curriculum and offer badges and certificates from the associated award scheme.'
    },
    {
      icon: <GiSwimfins className="text-5xl" />,
      title: 'Development',
      color: 'purple',
      sessions: 'Wednesdays 7:30 p.m. – 8:15 p.m., Sundays 3:00 p.m. – 3:30 p.m.',
      kit: 'Drink, goggles, hat, net bag with fins, kick-board and pull-buoy. Club hats must be worn for galas.',
      description: 'The swimmers in this group swim twice a week. The group is taught by a fully qualified teacher. The aim of this group is to continue working on stroke technique and to improve the swimmers\' stamina. Swimmers will continue to work on racing elements such as tumble turns and correct starts and finishes. At this stage we would encourage swimmers to enter external competitive galas. Swimmers in this group are expected to attend all training sessions regularly and to be on poolside ready to get in the water at the start of each session.'
    },
    {
      icon: <FaTrophy className="text-5xl" />,
      title: 'Juniors',
      color: 'orange',
      sessions: 'Wednesdays 8:15 p.m. – 9:30 p.m., Sundays 3:30 p.m. – 4:30 p.m.',
      kit: 'Drink, goggles, hat, net bag with fins, kick-board and pull-buoy. Club hats must be worn for galas.',
      description: 'This group trains three times a week. Entry into this group is purely dependent on ability and not age. Coaches will work with the swimmers in this group to develop good stroke technique, improve strength and stamina, fine tune racing techniques and tactics. Swimmers are expected to attend all training sessions regularly, represent the Club in local galas when selected, and compete individually in external galas.'
    },
    {
      icon: <FaMedal className="text-5xl" />,
      title: 'Seniors',
      color: 'red',
      sessions: 'Wednesdays 8:15 p.m. – 9:30 p.m., Sundays 3:30 p.m. – 4:30 p.m.',
      kit: 'Drink, goggles, hat, net bag with fins, kick-board and pull-buoy. Club hats must be worn at galas.',
      description: 'This group trains three times a week. Entry into this group is purely dependent on ability and not age. Coaches will work with the swimmers in this group to continue to develop good stroke technique, further improve strength and stamina, fine tune racing techniques and tactics. Swimmers are expected to attend all training sessions regularly, represent the Club in local galas when selected, and compete individually in external galas.'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'from-blue-50 to-blue-100',
        border: 'border-blue-500',
        icon: 'text-blue-600',
        text: 'text-blue-700'
      },
      green: {
        bg: 'from-green-50 to-green-100',
        border: 'border-green-500',
        icon: 'text-green-600',
        text: 'text-green-700'
      },
      purple: {
        bg: 'from-purple-50 to-purple-100',
        border: 'border-purple-500',
        icon: 'text-purple-600',
        text: 'text-purple-700'
      },
      orange: {
        bg: 'from-orange-50 to-orange-100',
        border: 'border-orange-500',
        icon: 'text-orange-600',
        text: 'text-orange-700'
      },
      red: {
        bg: 'from-primary-50 to-primary-100',
        border: 'border-primary-500',
        icon: 'text-primary-600',
        text: 'text-primary-700'
      }
    };
    return colors[color];
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Swimming Programs"
        description="Explore Marple Swimming Club's learn-to-swim and squad programs for all ages and abilities, from beginners to competitive swimmers."
        path="/programs"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-4">Swimming Programs</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Marple Swimming Club offers five distinct programs designed to support swimmers
            at every stage of their development, from first-time learners to competitive athletes.
          </p>
        </motion.div>

        {/* Coaching Team Highlight */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-500 rounded-2xl p-8 shadow-xl">
            <div className="flex items-center gap-4 mb-6">
              <FaStar className="text-5xl text-blue-600" />
              <h2 className="text-3xl font-bold text-blue-800">Our Coaching Team</h2>
            </div>
            <div className="space-y-4 text-gray-800">
              <p className="leading-relaxed">
                As well as our fantastic, committed and experienced Head Coaches <strong className="text-blue-700">Lucy and Dan</strong>, we have a team of teachers and poolside helpers that support our swimmers to learn and progress through the stages.
              </p>
              <p className="leading-relaxed">
                The team consist of club members who have progressed through the club themselves, and who swim in juniors and seniors. Swimmers who are aged 14 can volunteer as poolside helpers. As well as providing great support for the club, many of them use this as the foundation section for their Duke of Edinburgh Award.
              </p>
              <p className="leading-relaxed">
                When they reach the age of 16, they are required to complete a Swim England safeguarding course to ensure that they are aware of any safeguarding issues that might arise during sessions and how to deal with anything that causes concern and are subject to a DBS check which is arranged through the club.
              </p>
              <p className="leading-relaxed">
                At this point, each one of our older swimmers complete their <strong className="text-blue-700">SEQ Level 1 Swimming Assistant (Poolside)</strong> qualification to allow them to assist with swimming lessons, and <strong className="text-blue-700">SEQ Level 2 Teaching Swimming (Poolside)</strong> qualifications to allow them to plan, prepare, deliver and evaluate swimming lessons with support from Lucy and Dan.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Programs Grid */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-12">Our Five Programs</h2>
          <div className="space-y-6">
            {programs.map((program, index) => {
              const colors = getColorClasses(program.color);
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className={`bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Icon */}
                    <div className={`${colors.icon} flex-shrink-0 flex items-center justify-center`}>
                      {program.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className={`text-2xl md:text-3xl font-bold ${colors.text} mb-4`}>
                        {program.title}
                      </h3>

                      <p className="text-gray-800 leading-relaxed mb-4">
                        {program.description}
                      </p>

                      {/* Details */}
                      <div className="space-y-2">
                        {program.sessions && (
                          <div className="flex items-start gap-2">
                            <FaClock className={`${colors.icon} mt-1 flex-shrink-0`} />
                            <div>
                              <span className="font-semibold text-gray-900">Sessions: </span>
                              <span className="text-gray-700">{program.sessions}</span>
                            </div>
                          </div>
                        )}

                        {program.classSize && (
                          <div className="flex items-start gap-2">
                            <FaUsers className={`${colors.icon} mt-1 flex-shrink-0`} />
                            <div>
                              <span className="font-semibold text-gray-900">Class Size: </span>
                              <span className="text-gray-700">{program.classSize}</span>
                            </div>
                          </div>
                        )}

                        {program.kit && (
                          <div className="flex items-start gap-2">
                            <FaCheckCircle className={`${colors.icon} mt-1 flex-shrink-0`} />
                            <div>
                              <span className="font-semibold text-gray-900">Kit Required: </span>
                              <span className="text-gray-700">{program.kit}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Program Progression */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-8">Program Progression</h2>
          <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-12">
            Our programs are designed to provide a clear pathway for swimmers to progress
            from their first lesson through to competitive swimming. Each program builds
            on the skills learned in the previous level, ensuring steady development and
            continued motivation.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-lg border-2 border-primary-200 hover:shadow-2xl transition-all duration-300"
            >
              <div className="text-primary-600 mb-4">
                <FaCheckCircle className="text-4xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Assessment & Placement</h3>
              <p className="text-gray-700 leading-relaxed">
                New members receive an assessment to determine the most appropriate
                program level, ensuring they join a group that matches their current abilities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-2xl shadow-lg border-2 border-primary-200 hover:shadow-2xl transition-all duration-300"
            >
              <div className="text-primary-600 mb-4">
                <FaClock className="text-4xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Regular Reviews</h3>
              <p className="text-gray-700 leading-relaxed">
                Swimmers are regularly assessed for progression to the next level,
                with coaches providing feedback and guidance throughout their journey.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8 rounded-2xl shadow-lg border-2 border-primary-200 hover:shadow-2xl transition-all duration-300"
            >
              <div className="text-primary-600 mb-4">
                <FaStar className="text-4xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Flexible Movement</h3>
              <p className="text-gray-700 leading-relaxed">
                We understand that swimmers develop at different rates, so movement
                between programs is flexible and based on individual progress.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Schedule & Pricing */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 shadow-lg"
        >
          <h2 className="font-display text-3xl font-bold text-primary-700 text-center mb-6">Schedule & Pricing</h2>
          <p className="text-lg text-gray-800 text-center mb-6">
            Our programs run year-round with flexible scheduling options.
            Contact us for current pricing and availability.
          </p>
          <div className="max-w-md mx-auto bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-xl font-bold text-gray-900 mb-4">We Offer:</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <FaCheckCircle className="text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Monthly payment plans</span>
              </li>
              <li className="flex items-center gap-3">
                <FaCheckCircle className="text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Trial classes for new members</span>
              </li>
            </ul>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default Programs;
